/**
 * ConciliacaoCartoesPage Component
 * 
 * Página de listagem e processamento de conciliação de cartões.
 * Permite visualizar, criar e processar conciliações de cartões de crédito/débito.
 * 
 * Funcionalidades:
 * - Listagem de conciliações
 * - Criação de nova conciliação
 * - Processamento de conciliação
 * - Visualização de diferenças
 */

import { useState, useEffect, useCallback, useMemo } from 'react'
import { Plus, Search, CheckCircle, XCircle, AlertCircle, Edit, Copy, Check, Unlink, Filter, X, Download, CreditCard } from 'lucide-react'
import DataTable from '../../../../components/ERP/DataTable/DataTable'
import Modal from '../../../../components/ERP/Modal/Modal'
import FormField from '../../../../components/ERP/Form/FormField'
import Toast from '../../../../components/Toast/Toast'
import { useToast } from '../../../../hooks/useToast'
import { cardReconciliationService, paymentMethodService, paymentReceiptMethodService, cashStatementService } from '../../../../services/erpApi'
import './ConciliacaoCartoesPage.css'

function ConciliacaoCartoesPage() {
  const [conciliacoes, setConciliacoes] = useState([])
  const [loading, setLoading] = useState(true)
  const [modalOpen, setModalOpen] = useState(false)
  const [editingConciliacao, setEditingConciliacao] = useState(null)
  const [metodosPagamento, setMetodosPagamento] = useState([])
  const [formData, setFormData] = useState({
    reconciliationDate: new Date().toISOString().split('T')[0],
    paymentMethodId: '',
    expectedAmount: '',
    actualAmount: '',
    status: 'pending',
    observations: ''
  })
  const [errors, setErrors] = useState({})
  const [filters, setFilters] = useState({
    search: '',
    status: [],
    paymentMethodId: ''
  })
  const [isFilterOpen, setIsFilterOpen] = useState(false)
  
  // Estados para Toast
  const { toast, showToast, hideToast } = useToast()
  
  // Estado para controlar qual código foi copiado
  const [copiedId, setCopiedId] = useState(null)

  const loadConciliacoes = useCallback(async () => {
    setLoading(true)
    try {
      // Preparar filtros para o backend (aceita apenas um status)
      const backendFilters = {
        search: filters.search,
        paymentMethodId: filters.paymentMethodId || undefined,
        status: Array.isArray(filters.status) && filters.status.length === 1 
          ? filters.status[0] 
          : filters.status.length > 1 
            ? undefined // Se múltiplos, filtrar no frontend
            : filters.status
      }
      
      const response = await cardReconciliationService.getAll(backendFilters)
      if (response.success) {
        let data = response.data || []
        
        // Filtrar por múltiplos status no frontend se necessário
        if (Array.isArray(filters.status) && filters.status.length > 1) {
          data = data.filter(item => filters.status.includes(item.status))
        }
        
        // Filtrar por busca no frontend se necessário
        if (filters.search) {
          const searchLower = filters.search.toLowerCase()
          data = data.filter(item => 
            item.id?.toString().toLowerCase().includes(searchLower) ||
            item.payment_receipt_method_name?.toLowerCase().includes(searchLower) ||
            item.payment_method_name?.toLowerCase().includes(searchLower) ||
            item.observations?.toLowerCase().includes(searchLower)
          )
        }
        
        setConciliacoes(data)
      }
    } catch (error) {
      console.error('Erro ao carregar conciliações:', error)
    } finally {
      setLoading(false)
    }
  }, [filters])

  const loadMetodosPagamento = useCallback(async () => {
    try {
      // Buscar formas de recebimento (novo sistema)
      const responseReceipt = await paymentReceiptMethodService.getAll({ active: true })
      // Buscar métodos de pagamento (sistema antigo - fallback)
      const responsePayment = await paymentMethodService.getAll({ active: true })
      
      const formasRecebimento = responseReceipt.success ? (responseReceipt.data || []) : []
      const metodosPagamento = responsePayment.success ? (responsePayment.data || []) : []
      
      // Combinar ambos (formas de recebimento têm prioridade)
      setMetodosPagamento([...formasRecebimento, ...metodosPagamento])
    } catch (error) {
      console.error('Erro ao carregar métodos de pagamento:', error)
    }
  }, [])

  useEffect(() => {
    loadConciliacoes()
    loadMetodosPagamento()
  }, [loadConciliacoes, loadMetodosPagamento])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setErrors({})

    try {
      const dataToSend = {
        reconciliationDate: formData.reconciliationDate,
        paymentMethodId: formData.paymentMethodId || null,
        expectedAmount: parseFloat(formData.expectedAmount) || 0,
        actualAmount: parseFloat(formData.actualAmount) || 0,
        status: formData.status || 'pending',
        observations: formData.observations || null
      }

      let response
      if (editingConciliacao) {
        response = await cardReconciliationService.update(editingConciliacao.id, dataToSend)
      } else {
        response = await cardReconciliationService.create(dataToSend)
      }

      if (response.success) {
        setModalOpen(false)
        resetForm()
        loadConciliacoes()
      } else {
        if (response.errors && Array.isArray(response.errors)) {
          const errorObj = {}
          response.errors.forEach(err => {
            errorObj[err.param || err.path] = err.msg || err.message
          })
          setErrors(errorObj)
        } else {
          setErrors({ submit: response.message || 'Erro ao salvar conciliação' })
        }
      }
    } catch (error) {
      console.error('Erro ao salvar conciliação:', error)
      const errorMessage = error.response?.data?.message || 'Erro ao salvar conciliação'
      const errorDetails = error.response?.data?.errors
      
      if (errorDetails && Array.isArray(errorDetails)) {
        const errorObj = {}
        errorDetails.forEach(err => {
          errorObj[err.param || err.path] = err.msg || err.message
        })
        setErrors(errorObj)
      } else {
        setErrors({ submit: errorMessage })
      }
    }
  }


  const handleEdit = (conciliacao) => {
    setEditingConciliacao(conciliacao)
    setFormData({
      reconciliationDate: conciliacao.reconciliation_date ? conciliacao.reconciliation_date.split('T')[0] : new Date().toISOString().split('T')[0],
      paymentMethodId: conciliacao.payment_method_id || '',
      expectedAmount: conciliacao.expected_amount || '',
      actualAmount: conciliacao.actual_amount || '',
      status: conciliacao.status || 'pending',
      observations: conciliacao.observations || ''
    })
    setModalOpen(true)
  }

  const resetForm = () => {
    setFormData({
      reconciliationDate: new Date().toISOString().split('T')[0],
      paymentMethodId: '',
      expectedAmount: '',
      actualAmount: '',
      status: 'pending',
      observations: ''
    })
    setEditingConciliacao(null)
    setErrors({})
  }

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0)
  }

  const formatDate = (dateString) => {
    if (!dateString) return '-'
    const date = new Date(dateString)
    return date.toLocaleDateString('pt-BR')
  }

  const calculateDifference = (expected, actual) => {
    return (parseFloat(actual) || 0) - (parseFloat(expected) || 0)
  }

  const getStatusBadge = (status) => {
    const statusMap = {
      pending: { label: 'Pendente', class: 'pending', icon: AlertCircle },
      reconciled: { label: 'Conciliado', class: 'reconciled', icon: CheckCircle },
      discrepancy: { label: 'Divergência', class: 'discrepancy', icon: XCircle },
      cancelled: { label: 'Cancelado', class: 'cancelled', icon: XCircle }
    }
    const statusInfo = statusMap[status] || statusMap.pending
    const Icon = statusInfo.icon
    return (
      <span className={`status-badge ${statusInfo.class}`}>
        <Icon size={14} />
        {statusInfo.label}
      </span>
    )
  }

  const hasActiveFilters = useMemo(() => {
    return filters.search !== '' || 
           (Array.isArray(filters.status) && filters.status.length > 0) ||
           filters.paymentMethodId !== ''
  }, [filters])

  const resetFilters = () => {
    setFilters({
      search: '',
      status: [],
      paymentMethodId: ''
    })
  }

  const toggleStatusFilter = (statusValue) => {
    setFilters(prev => {
      const currentStatus = Array.isArray(prev.status) ? prev.status : [prev.status].filter(Boolean)
      const isSelected = currentStatus.includes(statusValue)
      
      let newStatus
      if (isSelected) {
        newStatus = currentStatus.filter(s => s !== statusValue)
      } else {
        newStatus = [...currentStatus, statusValue]
      }
      
      return { ...prev, status: newStatus }
    })
  }

  const calculateNetAmount = (grossAmount, taxRate) => {
    if (!grossAmount || !taxRate) return 0
    const gross = parseFloat(grossAmount) || 0
    const tax = parseFloat(taxRate) || 0
    return gross * (1 - tax / 100)
  }

  const handleCopyFitId = async (code, reconciliationId) => {
    if (!code) {
      showToast('Código não disponível', 'warning')
      return
    }
    
    try {
      await navigator.clipboard.writeText(code)
      setCopiedId(reconciliationId)
      showToast(`Código ${code} copiado! Use para buscar no extrato.`, 'success')
      setTimeout(() => setCopiedId(null), 2000)
    } catch (error) {
      console.error('Erro ao copiar:', error)
      showToast('Erro ao copiar código', 'error')
    }
  }

  const handleUnlinkReconciliation = async (reconciliation, transaction) => {
    if (!reconciliation || !transaction) return
    
    const confirmed = window.confirm(
      `Tem certeza que deseja desvincular esta conciliação da transação do extrato?\n\n` +
      `Conciliação: #${reconciliation.id}\n` +
      `Transação: ${transaction.description || 'N/A'}\n` +
      `Valor vinculado: ${formatCurrency(transaction.amount || 0)}\n\n` +
      `A conciliação voltará ao status correto baseado nos valores restantes.`
    )
    
    if (!confirmed) return
    
    try {
      const response = await cashStatementService.unlinkReconciliation(
        transaction.transaction_id,
        reconciliation.id,
        'card_reconciliation'
      )
      
      if (response && response.success) {
        showToast('Conciliação desvinculada com sucesso', 'success')
        loadConciliacoes() // Recarregar lista
      } else {
        showToast(response?.message || 'Erro ao desvincular conciliação', 'error')
      }
    } catch (error) {
      console.error('Erro ao desvincular conciliação:', error)
      showToast(error.response?.data?.message || 'Erro ao desvincular conciliação', 'error')
    }
  }

  const columns = [
    {
      key: 'reconciliation_date',
      label: 'DATA',
      render: (value) => formatDate(value),
      sortable: true
    },
    {
      key: 'payment_method',
      label: 'FORMA DE PAGAMENTO',
      render: (value, row) => {
        // Usar nome do backend se disponível (vem do JOIN)
        if (row.payment_receipt_method_name) {
          return row.payment_receipt_method_name
        }
        if (row.payment_method_name) {
          return row.payment_method_name
        }
        // Fallback: buscar na lista de métodos carregados
        const metodoRecebimento = metodosPagamento.find(m => m.id === row.payment_receipt_method_id)
        if (metodoRecebimento) {
          return metodoRecebimento.name
        }
        const metodo = metodosPagamento.find(m => m.id === row.payment_method_id)
        return metodo ? metodo.name : '-'
      }
    },
    {
      key: 'gross_amount',
      label: 'VALOR BRUTO',
      render: (value, row) => {
        // Se não tiver gross_amount, usar expected_amount como fallback
        const gross = value || row.expected_amount || 0
        return formatCurrency(gross)
      }
    },
    {
      key: 'tax_rate',
      label: 'TAXA',
      render: (value, row) => {
        // Usar tax_rate do backend se disponível
        if (value !== null && value !== undefined) {
          return `${parseFloat(value).toFixed(2)}%`
        }
        // Fallback: buscar da forma de recebimento
        const metodo = metodosPagamento.find(m => 
          m.id === row.payment_receipt_method_id || m.id === row.payment_method_id
        )
        const taxRate = metodo?.tax_rate || 0
        return `${parseFloat(taxRate).toFixed(2)}%`
      }
    },
    {
      key: 'system_net_amount',
      label: 'VALOR LIQUIDO SISTEMA',
      render: (value, row) => {
        // Usar system_net_amount do backend se disponível, senão calcular
        if (value !== null && value !== undefined) {
          return formatCurrency(value)
        }
        // Calcular valor líquido descontando a taxa
        const metodo = metodosPagamento.find(m => 
          m.id === row.payment_receipt_method_id || m.id === row.payment_method_id
        )
        const grossAmount = row.gross_amount || row.expected_amount || 0
        const taxRate = row.tax_rate || metodo?.tax_rate || 0
        const netAmount = calculateNetAmount(grossAmount, taxRate)
        return formatCurrency(netAmount)
      }
    },
    {
      key: 'bank_statement_net_amount',
      label: 'VALOR LIQUIDO EXTRATO BANCÁRIO',
      render: (value, row) => {
        // Verificar se há transações vinculadas
        const linkedTransactions = row.linked_transactions || []
        const hasLinks = linkedTransactions.length > 0
        
        // Se há transações vinculadas, usar o valor proporcional de cada transação
        // O valor proporcional vem do campo 'amount' de cada transação vinculada
        // Se não houver transações vinculadas, usar o valor proporcional calculado pelo backend
        let bankAmount = 0
        
        if (hasLinks && linkedTransactions.length > 0) {
          // Somar os valores proporcionais de todas as transações vinculadas
          // Cada transaction.amount já é o valor proporcional calculado pelo backend
          bankAmount = linkedTransactions.reduce((sum, transaction) => {
            return sum + (parseFloat(transaction.amount) || 0)
          }, 0)
        } else {
          // Se não há transações vinculadas, usar o valor proporcional calculado pelo backend
          // ou o valor direto se não houver cálculo proporcional
          bankAmount = row.proportional_bank_statement_net_amount !== undefined && row.proportional_bank_statement_net_amount !== null
            ? row.proportional_bank_statement_net_amount 
            : (value || row.actual_amount || 0)
        }
        
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
            <span>{formatCurrency(bankAmount)}</span>
            {hasLinks && (
              <>
                <span style={{ 
                  fontSize: '0.7rem', 
                  color: '#6b7280',
                  fontStyle: 'italic'
                }}>
                  ({linkedTransactions.length} vínculo{linkedTransactions.length > 1 ? 's' : ''})
                </span>
                {linkedTransactions.map((transaction, idx) => (
                  <button
                    key={idx}
                    onClick={(e) => {
                      e.stopPropagation()
                      handleUnlinkReconciliation(row, transaction)
                    }}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      padding: '0.25rem',
                      display: 'flex',
                      alignItems: 'center',
                      color: '#ef4444',
                      transition: 'color 0.2s',
                      marginLeft: '0.25rem'
                    }}
                    title={`Desvincular de: ${transaction.description || 'Transação'} (${formatCurrency(transaction.amount || 0)})`}
                  >
                    <Unlink size={14} />
                  </button>
                ))}
              </>
            )}
          </div>
        )
      }
    },
    {
      key: 'status',
      label: 'Status',
      render: (value) => getStatusBadge(value)
    },
    {
      key: 'id',
      label: 'CÓDIGO',
      render: (value, row) => {
        // Usar o ID da conciliação como código único para busca
        const reconciliationId = value || row.id
        if (!reconciliationId) {
          return <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>N/A</span>
        }
        const isCopied = copiedId === row.id
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <code style={{ 
              fontSize: '0.75rem', 
              background: '#f3f4f6', 
              padding: '0.25rem 0.5rem', 
              borderRadius: '4px',
              fontFamily: 'monospace',
              color: '#1f2937'
            }}>
              {reconciliationId}
            </code>
            <button
              onClick={(e) => {
                e.stopPropagation()
                handleCopyFitId(reconciliationId.toString(), row.id)
              }}
              style={{
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                padding: '0.25rem',
                display: 'flex',
                alignItems: 'center',
                color: isCopied ? '#10b981' : '#6b7280',
                transition: 'color 0.2s'
              }}
              title="Copiar código da conciliação para buscar no extrato"
            >
              {isCopied ? <Check size={14} /> : <Copy size={14} />}
            </button>
          </div>
        )
      }
    }
  ]

  const actions = [
    {
      label: 'Editar',
      icon: Edit,
      variant: 'primary',
      onClick: handleEdit
    }
  ]

  return (
    <div className="conciliacao-cartoes-page">
      <div className="page-header">
        <div>
          <h1>Conciliação de <span className="text-primary">Cartões</span></h1>
          <p>Gestão de Conciliação de Pagamentos por Cartão</p>
        </div>
        <button
          className="btn-primary"
          onClick={() => {
            resetForm()
            setModalOpen(true)
          }}
        >
          <Plus size={16} />
          Nova Conciliação
        </button>
      </div>

      {/* Main Table Container */}
      <div className="conciliacao-cartoes-table-container">
        <div className="conciliacao-cartoes-table-header">
          <div className="conciliacao-cartoes-search-wrapper">
            <Search className="conciliacao-cartoes-search-icon" size={18} />
            <input 
              type="text" 
              placeholder="Buscar conciliações..." 
              className="conciliacao-cartoes-search-input"
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            />
          </div>
          <div className="conciliacao-cartoes-table-actions">
            <button 
              onClick={() => setIsFilterOpen(true)}
              className={`conciliacao-cartoes-filter-button ${hasActiveFilters ? 'active' : ''}`}
            >
              <Filter size={14} /> Filtros
              {hasActiveFilters && (
                <span className="conciliacao-cartoes-filter-badge" />
              )}
            </button>
            <button className="conciliacao-cartoes-export-button">
              <Download size={14} /> Exportar
            </button>
          </div>
        </div>

        <div className="conciliacao-cartoes-table-wrapper">
          <DataTable
            columns={columns}
            data={conciliacoes}
            loading={loading}
            actions={actions}
            emptyMessage="Nenhuma conciliação encontrada"
          />
        </div>
      </div>

      {/* Side Panel: Filtros Avançados */}
      {isFilterOpen && (
        <>
          <div className="conciliacao-cartoes-filter-overlay" onClick={() => setIsFilterOpen(false)} />
          <aside className="conciliacao-cartoes-filter-sidebar conciliacao-cartoes-scrollbar">
            <div className="conciliacao-cartoes-filter-header">
              <div className="conciliacao-cartoes-filter-header-content">
                <div className="conciliacao-cartoes-filter-header-icon">
                  <Filter size={24} />
                </div>
                <div>
                  <h3 className="conciliacao-cartoes-filter-header-title">
                    Filtros
                  </h3>
                  <p className="conciliacao-cartoes-filter-header-subtitle">
                    Refinar resultados
                  </p>
                </div>
              </div>
              <button 
                onClick={() => setIsFilterOpen(false)} 
                className="conciliacao-cartoes-filter-close"
              >
                <X size={20} />
              </button>
            </div>

            <div className="conciliacao-cartoes-filter-content">
              {/* Status */}
              <div className="conciliacao-cartoes-filter-section">
                <h4 className="conciliacao-cartoes-filter-section-title">
                  Status (Selecione múltiplos)
                </h4>
                <div className="conciliacao-cartoes-filter-options">
                  {[
                    { id: 'pending', label: 'Pendente', icon: AlertCircle },
                    { id: 'reconciled', label: 'Conciliado', icon: CheckCircle },
                    { id: 'discrepancy', label: 'Divergência', icon: XCircle },
                    { id: 'cancelled', label: 'Cancelado', icon: X },
                  ].map(opt => {
                    const currentStatus = Array.isArray(filters.status) ? filters.status : [filters.status].filter(Boolean)
                    const isSelected = currentStatus.includes(opt.id)
                    return (
                      <button
                        key={opt.id}
                        onClick={() => toggleStatusFilter(opt.id)}
                        className={`conciliacao-cartoes-filter-option ${isSelected ? 'selected' : ''}`}
                      >
                        <div className="conciliacao-cartoes-filter-option-content">
                          <opt.icon 
                            size={18} 
                            className="conciliacao-cartoes-filter-option-icon"
                          />
                          <span className="conciliacao-cartoes-filter-option-label">
                            {opt.label}
                          </span>
                        </div>
                        {isSelected && (
                          <Check size={16} className="conciliacao-cartoes-filter-option-check" />
                        )}
                      </button>
                    )
                  })}
                </div>
              </div>

              {/* Forma de Pagamento */}
              <div className="conciliacao-cartoes-filter-section">
                <h4 className="conciliacao-cartoes-filter-section-title">
                  <CreditCard size={14} style={{ display: 'inline', marginRight: '0.5rem' }} />
                  Forma de Pagamento
                </h4>
                <select
                  value={filters.paymentMethodId}
                  onChange={(e) => setFilters({ ...filters, paymentMethodId: e.target.value })}
                  className="conciliacao-cartoes-filter-select"
                >
                  <option value="">Todas as formas</option>
                  {metodosPagamento.map(metodo => (
                    <option key={metodo.id} value={metodo.id}>{metodo.name}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="conciliacao-cartoes-filter-footer">
              <button 
                onClick={resetFilters}
                className="conciliacao-cartoes-filter-reset"
              >
                <X size={14} /> Limpar Filtros
              </button>
              <button 
                onClick={() => setIsFilterOpen(false)}
                className="conciliacao-cartoes-filter-apply"
              >
                Aplicar Filtros
              </button>
            </div>
          </aside>
        </>
      )}

      <Modal
        isOpen={modalOpen}
        onClose={() => {
          setModalOpen(false)
          resetForm()
        }}
        title={editingConciliacao ? 'Editar Conciliação' : 'Nova Conciliação'}
        size="medium"
        footer={
          <>
            <button
              className="btn-secondary"
              onClick={() => {
                setModalOpen(false)
                resetForm()
              }}
            >
              Cancelar
            </button>
            <button
              className="btn-primary"
              onClick={handleSubmit}
            >
              {editingConciliacao ? 'Salvar' : 'Criar'}
            </button>
          </>
        }
      >
        <form onSubmit={handleSubmit}>
          <FormField
            label="Data de Conciliação"
            name="reconciliationDate"
            type="date"
            value={formData.reconciliationDate}
            onChange={(e) => setFormData({ ...formData, reconciliationDate: e.target.value })}
            error={errors.reconciliationDate}
            required
          />

          <FormField
            label="Forma de Pagamento"
            name="paymentMethodId"
            type="select"
            value={formData.paymentMethodId}
            onChange={(e) => setFormData({ ...formData, paymentMethodId: e.target.value })}
            error={errors.paymentMethodId}
            options={[
              { value: '', label: 'Selecione uma forma de pagamento' },
              ...metodosPagamento.map(m => ({ value: m.id, label: m.name }))
            ]}
            required
          />

          <div className="form-row">
            <FormField
              label="Valor Esperado"
              name="expectedAmount"
              type="number"
              step="0.01"
              min="0"
              value={formData.expectedAmount}
              onChange={(e) => setFormData({ ...formData, expectedAmount: e.target.value })}
              error={errors.expectedAmount}
              required
            />

            <FormField
              label="Valor Real"
              name="actualAmount"
              type="number"
              step="0.01"
              min="0"
              value={formData.actualAmount}
              onChange={(e) => setFormData({ ...formData, actualAmount: e.target.value })}
              error={errors.actualAmount}
            />
          </div>

          {formData.expectedAmount && formData.actualAmount && (
            <div className="difference-info">
              <strong>Diferença:</strong>{' '}
              <span className={calculateDifference(formData.expectedAmount, formData.actualAmount) >= 0 ? 'positive' : 'negative'}>
                {calculateDifference(formData.expectedAmount, formData.actualAmount) >= 0 ? '+' : ''}
                {formatCurrency(calculateDifference(formData.expectedAmount, formData.actualAmount))}
              </span>
            </div>
          )}

          <FormField
            label="Status"
            name="status"
            type="select"
            value={formData.status}
            onChange={(e) => setFormData({ ...formData, status: e.target.value })}
            error={errors.status}
            options={[
              { value: 'pending', label: 'Pendente' },
              { value: 'reconciled', label: 'Conciliado' },
              { value: 'discrepancy', label: 'Divergência' },
              { value: 'cancelled', label: 'Cancelado' }
            ]}
          />

          <FormField
            label="Observações"
            name="observations"
            type="textarea"
            value={formData.observations}
            onChange={(e) => setFormData({ ...formData, observations: e.target.value })}
            error={errors.observations}
          />

          {errors.submit && (
            <div className="form-error-message">{errors.submit}</div>
          )}
        </form>
      </Modal>


      {/* Toast para notificações */}
      <Toast
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={hideToast}
      />
    </div>
  )
}

export default ConciliacaoCartoesPage

